import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import * as apps from '../../api/apps';
import type { AppListing } from '@/backend/types';
import { Chip, EmptyState, ListRow, Screen, SectionLabel } from '../../ui';

/**
 * One published app and everything its owner can do with it.
 *
 * The site URL comes from the backend (`siteUrl`) rather than being assembled here —
 * the client should not need to know the publishing origin, and a locally built URL was
 * how broken API-Gateway links used to reach people.
 */
export default function AppDetail() {
  const { appId = '' } = useParams();
  const navigate = useNavigate();
  const [app, setApp] = useState<AppListing | null>(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    let live = true;
    void apps
      .fetchApp(appId)
      .then((a) => {
        if (!live) return;
        if (a) setApp(a);
        else setMissing(true);
      })
      .catch(() => {
        if (live) setMissing(true);
      });
    return () => {
      live = false;
    };
  }, [appId]);

  if (missing) {
    return (
      <Screen title="App">
        <EmptyState
          icon="close"
          title="We can't find that app"
          body="It may have been unpublished."
          ctaTitle="Back to my apps"
          onCta={() => navigate('/my-apps')}
        />
      </Screen>
    );
  }

  if (!app) return <p className="type-body">Loading…</p>;

  return (
    <Screen title={app.name}>
      {app.description && <p className="type-body project-meta">{app.description}</p>}

      <div className="detail-chips">
        {app.webPublished ? <Chip label="Live" tone="success" /> : <Chip label="Not published" tone="neutral" />}
        {app.webVisibility === 'password' && <Chip label="Password" tone="primary" />}
        {app.personal && <Chip label="Yours" tone="primary" />}
      </div>

      {app.siteUrl && (
        <p className="type-body publish-url">
          <a href={app.siteUrl} target="_blank" rel="noreferrer">{app.siteUrl}</a>
        </p>
      )}

      <SectionLabel>Manage</SectionLabel>
      <div className="card clay-card detail-rows">
        <ListRow icon="account" label="People using it" onPress={() => navigate(`/users/${appId}`)} divider />
        <ListRow icon="password" label="Members and roles" onPress={() => navigate(`/members/${appId}`)} divider />
        <ListRow icon="editChat" label="Send a broadcast" onPress={() => navigate(`/broadcast/${appId}`)} />
      </div>
    </Screen>
  );
}
